import React, { useEffect, useState } from 'react';
import { Search, Filter, ChevronDown, Sparkles, Grid, List, Heart, MessageCircle, Share2 } from 'lucide-react';
import { AngelCard } from './AngelCard';
import { FloatingActionButton } from './FloatingActionButton';
import { ToysUpsell } from './ToysUpsell';
import { AngelFeedFilters } from './AngelFeedFilters';
interface Angel {
  id: string;
  name: string;
  username: string;
  avatar: string;
  tagline: string;
  membershipStatus?: string;
  online?: boolean;
  newContent?: boolean;
  nsfwStatus?: string;
  followers?: number;
}
interface AllAngelsGridProps {
  angels: Angel[];
  onAngelSelect: (angelId: string) => void;
}
export const AllAngelsGrid: React.FC<AllAngelsGridProps> = ({
  angels,
  onAngelSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredAngels, setFilteredAngels] = useState<Angel[]>(angels);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(false);
  const [showToyUpsell, setShowToyUpsell] = useState(true);
  // More angels data to simulate infinite loading
  const extraAngels = [{
    id: '7',
    name: 'Mia Khalifa',
    username: 'MiaKhalifa',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg',
    tagline: 'The girl who needs no introduction... 🔥',
    online: true,
    newContent: true,
    nsfwStatus: '🔥💦🍑',
    followers: 520000
  }, {
    id: '8',
    name: 'Zoey',
    username: 'ZoeyZooms',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5YZA8Ut1foPE5UkAGheBCE/image.png',
    tagline: 'Gamer girl with a wild side 🎮💕',
    online: false,
    newContent: true,
    nsfwStatus: '🔥',
    followers: 8700
  }, {
    id: '9',
    name: 'Bella Blue',
    username: 'BellaBlueX',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mSWACdMGnSXxSDMwRi1S6T/image.png',
    tagline: 'Feeling blue? Let me cheer you up 💙',
    online: true,
    newContent: false,
    nsfwStatus: '🔥💦',
    followers: 10300
  }, {
    id: '10',
    name: 'Summer',
    username: 'SummerHeat',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
    tagline: 'The flirty gamer cosplayer 🎮👙',
    online: true,
    newContent: true,
    nsfwStatus: '🔥',
    followers: 6500
  }];
  // Custom posts for each angel in the "For You" section
  const customPosts = {
    '1': [{
      id: 'custom1',
      content: 'Hey Mitch, I was thinking about you during my shower... want to see? 💦',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png',
      likes: 245,
      comments: 38
    }, {
      id: 'custom2',
      content: "Mitch, you've been on my mind all day. When are we having our next private session? 💋",
      likes: 187,
      comments: 29
    }],
    '2': [{
      id: 'custom3',
      content: "Mitch, I've prepared something special for our next game night. Can't wait to show you my new outfit 😈",
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png',
      likes: 312,
      comments: 47
    }],
    '3': [{
      id: 'custom4',
      content: "Mitch, I just got back from the gym and I'm all sweaty... want to help me shower? 💦",
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png',
      likes: 289,
      comments: 42
    }],
    '5': [{
      id: 'custom5',
      content: 'Just finished my yoga session, Mitch. My flexibility has improved so much... want a private demonstration? 💙',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png',
      likes: 178,
      comments: 31
    }],
    '11': [{
      id: 'custom6',
      content: "Mitch, I've been thinking about our last conversation... I have something special to show you tonight 💋",
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg',
      likes: 423,
      comments: 57
    }]
  };
  // Filter angels based on search query and category
  useEffect(() => {
    let result = [...angels, ...extraAngels];
    if (searchQuery) {
      result = result.filter(angel => angel.name.toLowerCase().includes(searchQuery.toLowerCase()) || angel.tagline.toLowerCase().includes(searchQuery.toLowerCase()) || angel.username.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (activeCategory !== 'all') {
      if (activeCategory === 'online') {
        result = result.filter(angel => angel.online);
      } else if (activeCategory === 'new_content') {
        result = result.filter(angel => angel.newContent);
      } else if (activeCategory === 'trending') {
        // For demo purposes, we'll consider angels with more followers as trending
        result = result.sort((a, b) => (b.followers || 0) - (a.followers || 0));
      } else if (activeCategory === 'following') {
        // Simulate followed angels (for demo, let's say we follow angels with IDs 1, 3, and 5)
        result = result.filter(angel => ['1', '3', '5', '11'].includes(angel.id));
      } else if (activeCategory === 'for_you') {
        // Personalized recommendations (for demo, prioritize angels with custom posts)
        result = result.sort((a, b) => {
          const aHasCustom = customPosts[a.id] ? 1 : 0;
          const bHasCustom = customPosts[b.id] ? 1 : 0;
          return bHasCustom - aHasCustom;
        });
      }
    }
    setFilteredAngels(result);
  }, [searchQuery, activeCategory, angels]);
  // Simulate loading more angels when scrolling
  const loadMoreAngels = () => {
    if (!loading) {
      setLoading(true);
      setTimeout(() => {
        // In a real app, you would fetch more angels from an API
        // For demo purposes, we'll just shuffle the existing angels
        const shuffledAngels = [...filteredAngels].sort(() => Math.random() - 0.5);
        setFilteredAngels([...filteredAngels, ...shuffledAngels.slice(0, 4)]);
        setLoading(false);
      }, 1000);
    }
  };
  // Handle scroll event for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 500) {
        loadMoreAngels();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filteredAngels]);
  // Handle angel selection with proper navigation
  const handleAngelSelect = (angelId: string) => {
    // Store the current section and set it to 'all_angels' to ensure we return here
    localStorage.setItem('previousSection', 'all_angels');
    // Create a global state flag that all angel profiles can check
    window.sessionStorage.setItem('returnToAllAngels', 'true');
    // Trigger a custom event to ensure proper loading of the angel feed
    const event = new CustomEvent('selectAngel', {
      detail: {
        angelId,
        returnToAllAngels: true,
        source: 'all_angels' // Add source information
      }
    });
    document.dispatchEvent(event);
    // Call the provided onAngelSelect function
    onAngelSelect(angelId);
  };
  // Handle action selection from FloatingActionButton
  const handleActionSelect = (action: string) => {
    console.log(`Action selected: ${action}`);
    // Implement action handling as needed
    if (action === 'fan_club') {
      setActiveCategory('following');
    }
  };
  // Categories for filtering
  const categories = [{
    id: 'all',
    label: 'All Angels',
    icon: '👼'
  }, {
    id: 'for_you',
    label: 'For You',
    icon: '✨'
  }, {
    id: 'following',
    label: 'Following',
    icon: '❤️'
  }, {
    id: 'online',
    label: 'Online Now',
    icon: '🟢'
  }, {
    id: 'new_content',
    label: 'New Content',
    icon: '🆕'
  }, {
    id: 'trending',
    label: 'Trending',
    icon: '🔥'
  }, {
    id: 'vip',
    label: 'VIP Only',
    icon: '💎'
  }];
  // Listen for navigation back from angel profile
  useEffect(() => {
    // Register a global function to handle back navigation that all angel profiles can call
    const handleNavigateBackToAllAngels = () => {
      console.log('Navigated back to all angels grid from specific event');
      // Force navigation to all_angels section
      const navigateEvent = new CustomEvent('navigate', {
        detail: {
          section: 'all_angels'
        }
      });
      document.dispatchEvent(navigateEvent);
    };
    // Listen for both general and specific back navigation events
    const handleNavigateBack = (event: Event) => {
      // Check if we need to return to all_angels grid
      if (window.sessionStorage.getItem('returnToAllAngels') === 'true') {
        console.log('Navigated back to all angels grid');
        handleNavigateBackToAllAngels();
        // Clear the flag after navigation
        window.sessionStorage.removeItem('returnToAllAngels');
      }
    };
    // Add event listeners
    document.addEventListener('navigateBackToDiscovery', handleNavigateBack);
    document.addEventListener('navigateBackToAllAngels', handleNavigateBackToAllAngels);
    // Make the function globally available for angel profile components
    window.navigateBackToAllAngels = handleNavigateBackToAllAngels;
    return () => {
      document.removeEventListener('navigateBackToDiscovery', handleNavigateBack);
      document.removeEventListener('navigateBackToAllAngels', handleNavigateBackToAllAngels);
      // Clean up global function
      delete window.navigateBackToAllAngels;
    };
  }, []);
  return <div className="w-full" data-id="element-1265">
      {/* Header with title and view toggle */}
      <div className="flex justify-between items-center mb-4" data-id="element-1266">
        <div className="flex items-center gap-2" data-id="element-1267">
          <Sparkles size={20} className="text-pink-500" data-id="element-1268" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1269">
            Discover Angels
          </h1>
        </div>
        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1" data-id="element-1270">
          <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'}`} title="Grid View" data-id="element-1271">
            <Grid size={16} data-id="element-1272" />
          </button>
          <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'}`} title="List View" data-id="element-1273">
            <List size={16} data-id="element-1274" />
          </button>
        </div>
      </div>

      {/* Search and filter section */}
      <div className="mb-6 space-y-4" data-id="element-1275">
        {/* Search input */}
        <div className="relative" data-id="element-1276">
          <input type="text" placeholder="Search angels by name or interests..." className="w-full h-10 pl-10 pr-4 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900 dark:text-white" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1277" />
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" data-id="element-1278" />
        </div>

        {/* Category filters */}
        <div className="flex overflow-x-auto py-2 gap-2 scrollbar-hide" data-id="element-1279">
          {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full whitespace-nowrap transition-all ${activeCategory === category.id ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-1280">
              <span data-id="element-1281">{category.icon}</span>
              <span data-id="element-1282">{category.label}</span>
            </button>)}
        </div>

        {/* Additional filters similar to AngelFeedFilters */}
        {activeCategory === 'for_you' && <AngelFeedFilters activeFilter={activeFilter} setActiveFilter={setActiveFilter} data-id="element-1283" />}
      </div>

      {/* For You custom posts section */}
      {activeCategory === 'for_you' && <div className="mb-6 space-y-4" data-id="element-1284">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1285">
            <Heart size={18} className="text-pink-500" fill="currentColor" data-id="element-1286" />
            <span data-id="element-1287">Personalized Messages</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1288">
            {Object.entries(customPosts).map(([angelId, posts]) => {
          const angel = [...angels, ...extraAngels].find(a => a.id === angelId);
          if (!angel) return null;
          return posts.map(post => <div key={post.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-1289">
                    <div className="p-3 flex items-center gap-2 border-b border-gray-200 dark:border-gray-700" data-id="element-1290">
                      <div className="w-10 h-10 rounded-full overflow-hidden cursor-pointer" onClick={() => handleAngelSelect(angelId)} data-id="element-1291">
                        <img src={angel.avatar} alt={angel.name} className="w-full h-full object-cover" data-id="element-1292" />
                      </div>
                      <div className="cursor-pointer" onClick={() => handleAngelSelect(angelId)} data-id="element-1293">
                        <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-1294">
                          {angel.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-1295">
                          Just for you • 2h ago
                        </p>
                      </div>
                    </div>
                    <div className="p-3" data-id="element-1296">
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2" data-id="element-1297">
                        {post.content}
                      </p>
                      {post.image && <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden mb-3" data-id="element-1298">
                          <img src={post.image} alt="Post content" className="w-full h-full object-cover" data-id="element-1299" />
                        </div>}
                      <div className="flex items-center justify-between mt-2" data-id="element-1300">
                        <div className="flex items-center gap-4" data-id="element-1301">
                          <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-pink-500 dark:hover:text-pink-400 transition-colors" data-id="element-1302">
                            <Heart size={16} data-id="element-1303" />
                            <span className="text-xs" data-id="element-1304">{post.likes}</span>
                          </button>
                          <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors" data-id="element-1305">
                            <MessageCircle size={16} data-id="element-1306" />
                            <span className="text-xs" data-id="element-1307">{post.comments}</span>
                          </button>
                          <button className="flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-colors" data-id="element-1308">
                            <Share2 size={16} data-id="element-1309" />
                          </button>
                        </div>
                        <button className="text-xs px-3 py-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-full" onClick={() => handleAngelSelect(angelId)} data-id="element-1310">
                          View Profile
                        </button>
                      </div>
                    </div>
                  </div>);
        }).flat()}
          </div>
        </div>}

      {/* Toys Upsell Section */}
      {showToyUpsell && <div className="mb-6" data-id="element-1311">
          <ToysUpsell style="card" data-id="element-1312" />
        </div>}

      {/* Angels grid */}
      <div className={viewMode === 'grid' ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4' : 'space-y-4'} data-id="element-1313">
        {filteredAngels.map(angel => <AngelCard key={angel.id} angel={angel} viewMode={viewMode} onClick={() => handleAngelSelect(angel.id)} data-id="element-1314" />)}
      </div>

      {/* Loading indicator */}
      {loading && <div className="flex justify-center items-center py-8" data-id="element-1315">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-pink-500 rounded-full animate-spin" data-id="element-1316"></div>
          <span className="ml-2 text-gray-500 dark:text-gray-400" data-id="element-1317">
            Loading more angels...
          </span>
        </div>}

      {/* Floating Action Button */}
      <FloatingActionButton onActionSelect={handleActionSelect} data-id="element-1318" />

      <style jsx data-id="element-1319">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
};